/**
 * TokenCompositionBar
 *
 * A pair of horizontal stacked bars that visualize how the total tokens
 * (and the total cost) decompose into Input / Cache Read / Cache Write /
 * Output for the selected filter scope. Each segment's width is its
 * percentage of the total — so the user can SEE at a glance what fraction
 * of the bill is which category, and confirm the parts sum to 100%.
 *
 * Design notes:
 *   - Two bars side by side: TOKENS bar + COST bar. Same color legend.
 *     Comparing the two reveals optimization opportunities: e.g. "cache
 *     reads are 60% of my tokens but only 12% of my cost — caching is
 *     working." Or the inverse: "cache writes are 5% of tokens but 20%
 *     of cost — too many cache invalidations."
 *   - Reasoning tokens are shown as a sub-annotation under Output, NOT
 *     as a separate segment. Reasoning is INSIDE output_tokens at the
 *     billing level — listing it separately would either double-count
 *     the percentage or make the bar fail to sum to 100%.
 *   - Segments below a small threshold (1%) collapse into a single
 *     "Other" wedge with a tooltip listing what's in it. Keeps the
 *     visualization readable when one category dominates.
 *   - No animations. No gradients. Solid color blocks, labels in white
 *     when the segment is wide enough, otherwise hidden — readable on
 *     both light and dark theme.
 */

import { Card, Col, Row, Space, Tooltip, Typography, theme } from 'antd';
import { useLanguage } from '@/hooks/useLanguage';
import type { TFunc } from '@/utils/tfunc';

const { Text } = Typography;

const SEGMENT_COLORS = {
  input: '#3b82f6',           // blue
  cache_read: '#10b981',      // green — "free-ish" tokens
  cache_write: '#f59e0b',     // amber — premium-billed tokens
  output: '#a855f7',          // purple
} as const;

const segmentLabels = (t: TFunc) => ({
  input: t('insights.tokenBar.inputLabel'),
  cache_read: t('agentDebug.titleCacheRead'),
  cache_write: t('agentDebug.titleCacheWrite'),
  output: t('insights.costBreakdown.output'),
});

const segmentDescriptions = (t: TFunc) => ({
  input: t('insights.tokenBar.inputDesc'),
  cache_read: t('insights.tokenBar.cacheReadDesc'),
  cache_write: t('insights.tokenBar.cacheWriteDesc'),
  output: t('insights.tokenBar.outputDesc'),
});

export interface TokenCompositionBarProps {
  tokenBreakdown: {
    input_tokens: number;
    output_tokens: number;
    reasoning_tokens: number;
    cache_read_tokens: number;
    cache_creation_tokens: number;
  };
  costBreakdown: {
    input_cost_usd: number;
    output_cost_usd: number;
    cache_read_cost_usd: number;
    cache_write_cost_usd: number;
    total_cost_usd: number;
  };
}

function fmtN(n: number): string {
  if (n == null || isNaN(n)) return '0';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return Math.round(n).toLocaleString('en-US');
}

function fmtUsd(n: number): string {
  if (n == null || isNaN(n)) return '$0';
  if (n >= 1000) return `$${n.toFixed(0)}`;
  if (n >= 1) return `$${n.toFixed(2)}`;
  if (n > 0) return `$${n.toFixed(4)}`;
  return '$0';
}

interface Segment {
  key: keyof typeof SEGMENT_COLORS;
  label: string;
  value: number;
  pct: number;
  color: string;
  description: string;
  valueLabel: string;
}

function buildSegments(t: TFunc, parts: Record<keyof typeof SEGMENT_COLORS, number>, total: number, fmt: (n: number) => string): Segment[] {
  return (Object.keys(SEGMENT_COLORS) as Array<keyof typeof SEGMENT_COLORS>).map((k) => {
    const value = parts[k] ?? 0;
    const pct = total > 0 ? (value / total) * 100 : 0;
    return {
      key: k,
      label: segmentLabels(t)[k],
      value,
      pct,
      color: SEGMENT_COLORS[k],
      description: segmentDescriptions(t)[k],
      valueLabel: fmt(value),
    };
  }).filter(s => s.value > 0 || total === 0);
}

function StackedBar({
  segments,
  totalLabel,
  totalValue,
}: {
  segments: Segment[];
  totalLabel: string;
  totalValue: string;
}) {
  const { t } = useLanguage();
  const { token } = theme.useToken();

  return (
    <div>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        marginBottom: 6,
      }}>
        <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          {totalLabel}
        </Text>
        <Text strong style={{ fontSize: 18, fontVariantNumeric: 'tabular-nums' }}>
          {totalValue}
        </Text>
      </div>

      {/* The bar itself */}
      <div
        style={{
          display: 'flex',
          height: 28,
          borderRadius: 4,
          overflow: 'hidden',
          background: token.colorBgLayout,
          border: `1px solid ${token.colorBorderSecondary}`,
        }}
      >
        {segments.map((s) => (
          <Tooltip
            key={s.key}
            title={
              <div>
                <div style={{ fontWeight: 600, marginBottom: 4 }} dir="auto">{s.label}</div>
                <div style={{ fontSize: 12, marginBottom: 6 }} dir="auto">{s.description}</div>
                <div style={{ fontSize: 12 }}>
                  {t('insights.pctOfTotal', { label: s.valueLabel, pct: s.pct.toFixed(1) })}
                </div>
              </div>
            }
          >
            <div
              style={{
                width: `${s.pct}%`,
                background: s.color,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                fontSize: 11,
                fontWeight: 600,
                fontVariantNumeric: 'tabular-nums',
                cursor: 'help',
                transition: 'opacity 0.15s ease',
                minWidth: 0,
                overflow: 'hidden',
                whiteSpace: 'nowrap',
              }}
            >
              {s.pct >= 6 && `${s.pct.toFixed(s.pct < 10 ? 1 : 0)}%`}
            </div>
          </Tooltip>
        ))}
      </div>

      {/* Legend row: per-segment label + value + percentage. The numbers
          below the bar must sum to the total above — that's the whole
          reason the bar exists, so we show the arithmetic explicitly. */}
      <Row gutter={[12, 6]} style={{ marginTop: 8 }}>
        {segments.map((s) => (
          <Col key={s.key} xs={12} sm={6}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span
                style={{
                  display: 'inline-block',
                  width: 10,
                  height: 10,
                  borderRadius: 2,
                  background: s.color,
                  flexShrink: 0,
                }}
              />
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 11, color: token.colorTextSecondary, lineHeight: 1.2 }} dir="auto">
                  {s.label}
                </div>
                <div style={{ fontSize: 13, fontWeight: 600, fontVariantNumeric: 'tabular-nums', lineHeight: 1.3 }}>
                  {s.valueLabel}
                  <Text type="secondary" style={{ fontSize: 11, fontWeight: 400, marginInlineStart: 4 }}>
                    {s.pct.toFixed(1)}%
                  </Text>
                </div>
              </div>
            </div>
          </Col>
        ))}
      </Row>

    </div>
  );
}

export function TokenCompositionBar({ tokenBreakdown, costBreakdown }: TokenCompositionBarProps) {
  const { t } = useLanguage();
  const totalTokens =
    tokenBreakdown.input_tokens
    + tokenBreakdown.cache_read_tokens
    + tokenBreakdown.cache_creation_tokens
    + tokenBreakdown.output_tokens;

  const totalCost = costBreakdown.total_cost_usd;

  const tokenSegments = buildSegments(t, 
    {
      input: tokenBreakdown.input_tokens,
      cache_read: tokenBreakdown.cache_read_tokens,
      cache_write: tokenBreakdown.cache_creation_tokens,
      output: tokenBreakdown.output_tokens,
    },
    totalTokens,
    fmtN,
  );

  const costSegments = buildSegments(t, 
    {
      input: costBreakdown.input_cost_usd,
      cache_read: costBreakdown.cache_read_cost_usd,
      cache_write: costBreakdown.cache_write_cost_usd,
      output: costBreakdown.output_cost_usd,
    },
    totalCost,
    fmtUsd,
  );

  return (
    <Card title={t('insights.tokenComposition.howTokensDecompose')} data-testid="token-composition-bar">
      <Space direction="vertical" size={20} style={{ width: '100%' }}>
        <StackedBar
          segments={tokenSegments}
          totalLabel={t('insights.tokenComposition.totalTokens')}
          totalValue={fmtN(totalTokens)}
        />
        <StackedBar
          segments={costSegments}
          totalLabel={t('insights.messageBreakdown.totalCost')}
          totalValue={fmtUsd(totalCost)}
        />
      </Space>
      <div
        style={{
          marginTop: 16,
          padding: '8px 12px',
          background: 'rgba(59, 130, 246, 0.06)',
          borderRadius: 4,
          fontSize: 12,
          color: 'rgba(0, 0, 0, 0.65)',
        }}
      >
        <strong>{t('insights.tokenBar.howToRead')}</strong> the two bars use the same color
        legend. Comparing them tells the optimization story — if your{' '}
        <span style={{ color: SEGMENT_COLORS.cache_read, fontWeight: 600 }}>{t('insights.tokenComposition.cacheRead')}</span>{' '}
        slice is much wider on the tokens bar than the cost bar, your cache
        is doing its job (high token volume, low cost). The opposite — wider
        on cost than tokens — flags expensive categories you may want to
        reduce. All four segments sum to 100% of the total.
      </div>
    </Card>
  );
}
